import { buildSheet } from '../pricing/editionSheet';
import { toSeedRow } from './planWrites';
import type { PriceSheet } from '../pricing/editionSheet';
import type { CatalogSeed } from '../pricing/seedBuilder';
import type { PlannedRow } from './costDraft';

/**
 * What moved. `'price'` is the NET price in DOLLARS (`SkuSheetEntry.prices`);
 * `'discount'` is the discount FRACTION (`SkuSheetEntry.discounts`, 0.3 = 30%).
 *
 * The second kind exists because of whole-branch review finding C1: the sheet
 * carries two independent things a catalog write can move, and this function
 * diffed only one of them. `deriveHwPricing` derives `discountCents` — the net
 * price — from the component rollup alone, so a change to `discountBp` moves
 * `listCents` and `SkuSheetEntry.discounts` and NOTHING in `prices`. A review
 * that diffed only `prices` therefore reported "nothing moves" for a change
 * that rewrote 358 discount cells in the real AIC sheet, and the reviewer
 * approved an empty impact table. `planWrites` now refuses to author a pct row
 * at all, but that closes only today's route in: any future discount-authoring
 * path would be invisible to review for exactly the same reason, so the
 * preview is made to see the whole sheet rather than trusted to only ever be
 * handed rows that move half of it.
 */
export type ImpactKind = 'price' | 'discount';

export interface ImpactRow {
  productSku: string;
  quarter: string;
  /** DOLLARS when `kind` is 'price', a FRACTION when it is 'discount'. The
   *  units differ, so every consumer must branch on `kind` before formatting —
   *  rendering 0.3 through a dollar formatter reads as "$0.30 off". */
  kind: ImpactKind;
  before: number | null;
  after: number | null;
  delta: number | null;
}

/** Price rows first for a given (sku, quarter): the net price is what a PM
 *  came to check, the discount is context for it. */
const KIND_RANK: Record<ImpactKind, number> = { price: 0, discount: 1 };

/**
 * Runs the SAME buildSheet publish runs, over a copy of the seed with the
 * planned rows applied, and diffs the two sheets.
 *
 * Calling buildSheet rather than reimplementing the arithmetic is the whole
 * point: if preview had its own math, the PM could approve one price ripple
 * while publish computed another, and both halves would look internally
 * consistent — no other test in the system would catch it.
 */
export function previewImpact(
  planned: PlannedRow[], seed: CatalogSeed, quarters: string[], asOf?: string,
): ImpactRow[] {
  if (planned.length === 0) return [];

  const before = buildSheet(seed, quarters, asOf);
  // Staged through planWrites' OWN toSeedRow, not a literal built here. Two
  // copies of "how a planned row becomes a seed row" is exactly the divergence
  // Task 14's anti-divergence gate exists to catch — and the inline literal
  // hardcoded recordedAt: '9999-…', which under any asOf is > asOf, so
  // resolveAt (resolveAt.ts:53) filtered every staged row straight back out and
  // this function returned [] — "no prices move" — for ANY change.
  const after = buildSheet({
    rows: [
      ...seed.rows,
      ...planned.map((p) => toSeedRow(p, seed.rows[0]?.vendor ?? 'AIC', asOf)),
    ],
    compositions: seed.compositions,
  }, quarters, asOf);

  return diffSheets(before, after, quarters);
}

/**
 * Every movement between two sheets, over both of the things a sheet carries.
 *
 * Deliberately NOT exported. Task 14's anti-divergence gate builds its own
 * diff, by hand, over the same two sheets — the same reason its
 * `applyPlannedToSeed` goes through the real `buildCreateInputs` rather than
 * reusing `toSeedRow`. A gate that imported this would compare preview against
 * preview: making this function blind to `discounts` would make BOTH sides of
 * its set-equality assertion blind in the same instant, and the gate would go
 * on passing. That is measured, not assumed — the first draft of the
 * strengthened gate did import it, and a price-only mutation slipped past two
 * of its three cases.
 *
 * `toFixed` on the deltas is float hygiene, not rounding of a stored value:
 * both inputs are already the exact quantities the sheet holds (dollars via
 * `centsToDollars`, fractions via `bp / 10000`), and subtracting two of them
 * yields things like 0.020000000000000018. Two decimals for dollars — a sheet
 * price has no more — and six for a fraction, which is four more than basis
 * points can express.
 */
function diffSheets(
  before: PriceSheet, after: PriceSheet, quarters: string[],
): ImpactRow[] {
  const out: ImpactRow[] = [];
  for (const sku of new Set([...Object.keys(before), ...Object.keys(after)])) {
    for (const quarter of quarters) {
      const b = before[sku]?.prices[quarter] ?? null;
      const a = after[sku]?.prices[quarter] ?? null;
      if (b !== a) {
        out.push({ productSku: sku, quarter, kind: 'price', before: b, after: a,
                   delta: b !== null && a !== null ? Number((a - b).toFixed(2)) : null });
      }
      // `discounts` is OPTIONAL on SkuSheetEntry (editionSheet.ts:41) — an
      // edition published before per-quarter discounts has none — so this
      // reads through `?.` and treats absence as null rather than assuming
      // buildSheet always populates it.
      const bd = before[sku]?.discounts?.[quarter] ?? null;
      const ad = after[sku]?.discounts?.[quarter] ?? null;
      if (bd !== ad) {
        out.push({ productSku: sku, quarter, kind: 'discount', before: bd, after: ad,
                   delta: bd !== null && ad !== null ? Number((ad - bd).toFixed(6)) : null });
      }
    }
  }
  return out.sort((x, y) =>
    x.productSku.localeCompare(y.productSku)
    || x.quarter.localeCompare(y.quarter)
    || KIND_RANK[x.kind] - KIND_RANK[y.kind]);
}
